import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Pagination, PermissionSetService } from 'blg-akaun-ts-lib';
import * as moment from 'moment';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { PermSetListingSearchModel } from '../../../../../models/advanced-search-models/perm-set-listing-search.model';
import { PermissionSetModel, PermSetLine } from '../../../../../models/perm-set.model';
import { gridOptionsServerSide, pageFiltering, pageSorting } from '../../../../../listing.utils';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { Observable, } from 'rxjs';
import { SubSink } from 'subsink2';
import { PermSetActions } from '../../../perm-set-controller/actions';
import { PermSetSelectors } from '../../../perm-set-controller/selectors';
import { PermissionStates } from '../../../permission-controller/root';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-perm-set-listing',
  templateUrl: './perm-set-listing.component.html',
  styleUrls: ['./perm-set-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermSetListingComponent extends ViewColumnComponent {


  protected readonly index = 0;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  toggleColumn$: Observable<boolean>;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  searchModel = PermSetListingSearchModel;
  generalSearchValue: string = '';
  advancedSearchModel: any = {
    code: '',
    name: '',
    description: '',
  };

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'single';

  // this applet guid
  appletGuid = sessionStorage.getItem('appletGuid');

  @ViewChild(PaginationComponent, {static: false}) private paginationComponent: PaginationComponent;

  //translation variable
  code_text: string;
  name_text: string;
  created_date_text: string;
  modified_date_text: string;
  create_button: string;

  constructor(
    private permissionSetService: PermissionSetService,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.code_text = res.code;
        this.name_text = res.name;
        this.created_date_text = res.created_date;
        this.modified_date_text = res.modified_date;
        this.create_button = res.create_button_nocaps;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.created_date_text = 'Created Date';
      this.modified_date_text = 'Modified Date';
      this.create_button = 'Create';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });

    // define column definitions
    this.columnsDefs = [
      {
        headerName: this.code_text,
        field: 'app_perm_set_hdr.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'app_perm_set_hdr.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.created_date_text,
        field: 'app_perm_set_hdr.created_date',
        headerClass: "text-center",
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      },
      {
        headerName: this.modified_date_text,
        field: 'app_perm_set_hdr.updated_date',
        headerClass: "text-center",
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      }
    ]
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
   onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: true,
        deactivateList: false
      });
    // trigger the add permission set component to be shown
    this.viewColFacade.onNextAndReset(this.index, 1);
  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    this.store.select(PermSetSelectors.selectUpdatePermSetListing)
    .subscribe(x => {
      if (x) {
        if (this.gridApi.getDisplayedRowCount() > 0) {
          this.gridApi.refreshServerSideStore();
        } else {
          this.gridApi.refreshServerSideStore(
            {purge: true}
          )
        }
        this.store.dispatch(PermSetActions.setUpdatePermSetListing({update: false}));
      }
    })
  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
   getDatasource() {
    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    const sortCriteria = [
      { columnName: 'orderBy', value: 'updated_date' },
      { columnName: 'order', value: 'DESC' }
    ];

    return {
      getRows: grid => {
        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const agFilter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        // if (Object.keys(filterModel).length === 0 && sortModel.length === 0) {
        //   offset = grid.request.startRow;
        //   limit = grid.request.endRow - offset;
        // }

        offset = grid.request.startRow;
        limit = grid.request.endRow - offset;

        const pagination = new Pagination(offset, limit, [], sortCriteria);

        pagination.conditionalCriteria = [
          {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
          {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
        ];

        if (this.generalSearchValue) {
          // Only include general search if no advanced search criteria are present
          pagination.conditionalCriteria.push({
            columnName: 'search_word',
            operator: '=',
            value: this.generalSearchValue,
          });
        }

        Object.keys(this.advancedSearchModel).forEach((key) => {
          const value = this.advancedSearchModel[key];
          if (value) {
            // Handle other fields normally
            pagination.conditionalCriteria.push({
              columnName: key,
              operator: '=',
              value: value,
            });
          }
        });

        this.subSink.sink = this.permissionSetService.getByCriteria(pagination, this.apiVisa)
          .subscribe(a => {
            // put in data to the grid
            const data = sortOn(a.data).filter(entity => agFilter.by(entity));
            const totalRecords = agFilter.isFiltering ? a.totalRecords :  data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) {
              this.paginationComponent.firstPage();
            }
            grid.success({
              rowData: data,
              rowCount: totalRecords
            });
          }, err => {
            grid.fail();
          });
      }
    };
  }

  /**
   * This function is called every time the user clicks on a row in the
   * AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // dispatch ngrx actions
    const permSetModel = new PermissionSetModel();
    permSetModel.app_perm_set_hdr = entity.app_perm_set_hdr;
    permSetModel.permSetLines = entity.app_perm_set_lines.map(line => {
      const permSetLine = new PermSetLine();

      permSetLine.app_perm_set_line = line;

      const allTargets = line.target_options_json.target_hdr_table_view_name.split(",").map(x => x.trim());
      const allTargetTables = line.target_options_json.target_hdr_table.split(",").map(x => x.trim());
      const index = allTargetTables.indexOf(line.target_options_json.default_target_hdr_table);
      permSetLine.default_target_view_name = allTargets[index];

      return permSetLine;
    });

    // select permission set
    this.store.dispatch(PermSetActions.selectPermSet({permSet: permSetModel}));

    // deactivate add and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: false,
          deactivateList: true
        });
      // trigger the next view column
      this.viewColFacade.onNextAndReset(this.index, 2);
    }
  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
   onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

    // General search handler
    onSearch(value: string): void {
      this.generalSearchValue = value;
      this.advancedSearchModel = {
        code: '',
        name: '',
        description: '',
      };

      // Refresh grid with general search value
      this.gridApi.refreshServerSideStore({ purge: true });
    }

    // Advanced search handler
    onAdvancedSearch(criteria: any): void {
      this.generalSearchValue = '';
      this.advancedSearchModel = criteria;

      // Refresh grid with advanced search values
      this.gridApi.refreshServerSideStore({ purge: true });
    }

    // Reset handler
    onReset(): void {
      this.generalSearchValue = '';
      this.advancedSearchModel = {
        code: '',
        name: '',
        description: '',
      };

      // Refresh grid with no filters
      this.gridApi.refreshServerSideStore({ purge: true });
    }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
